import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import { alsEuro } from '../betrag';
import { Reiterleiste } from '../komponenten/Akte';
import { istPdfName, ladeUndReiche } from '../komponenten/datei';
import { Thema } from '../komponenten/Einstellungen';
import { Meldung } from '../komponenten/Meldung';
import {
  festschreibung as ladeFestschreibung,
  konfiguration as ladeKonfiguration,
  kontrollen as ladeKontrollen,
  datentraeger as ladeDatentraeger,
  datentraegerBereiche as ladeDatenbereiche,
  datentraegerDatei as holeDatei,
  datentraegerErzeugen as erzeugeDatentraeger,
  rechte as ladeRechte,
  z2Auswertung as ladeAuswertung,
  z2Merkmale as ladeMerkmale,
  type Konfigurationsaenderung,
  type Kontrollnachweis,
  type Monatsstand,
  type Exportvermerk,
  type Rechtezeile,
  type Z2Beleg,
  type Z2Merkmale,
  type Z2Summe,
} from '../api/betriebspruefung';

type Reiter =
  | 'auswertung'
  | 'datentraeger'
  | 'festschreibung'
  | 'kontrollen'
  | 'konfiguration'
  | 'rechte';

const REITER: readonly { kennung: Reiter; beschriftung: string }[] = [
  /*
   * DER MITTELBARE ZUGRIFF STEHT VORN (BP-03, Z2).
   *
   * Er ist das, wonach eine Betriebspruefung zuerst fragt: Umsaetze je
   * Zeitraum und Steuersatz. Die uebrigen vier Reiter sind Nachweise DARUEBER,
   * wie das Haus arbeitet — wichtig, aber nicht die erste Frage.
   */
  { kennung: 'auswertung', beschriftung: 'Auswertung (Z2)' },
  { kennung: 'datentraeger', beschriftung: 'Datenträger (Z3)' },
  { kennung: 'festschreibung', beschriftung: 'Festschreibung' },
  { kennung: 'kontrollen', beschriftung: 'Kontrollsystem' },
  { kennung: 'konfiguration', beschriftung: 'Konfiguration' },
  { kennung: 'rechte', beschriftung: 'Berechtigungen' },
];

/** Die Arten aus BP-42, ausgeschrieben. In der Datenbank steht die Kennung. */
const KONTROLLART: Readonly<Record<string, string>> = {
  freigabe: 'Freigabe',
  vieraugen: 'Vier-Augen-Prinzip',
  plausibilitaet: 'Plausibilitätsprüfung',
  vollstaendigkeit: 'Vollständigkeitsprüfung',
  abstimmung: 'Abstimmlauf',
};

const VORGANG: Readonly<Record<string, string>> = {
  INSERT: 'angelegt',
  UPDATE: 'geändert',
  DELETE: 'entfernt',
};

function tag(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });
}

function zeitpunkt(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

function monatstext(monat: string): string {
  const [jahr, m] = monat.split('-');
  return `${m ?? '??'}/${jahr ?? '????'}`;
}

/**
 * ============================================================================
 * DIE NACHWEISE FÜR DIE BETRIEBSPRÜFUNG (BP-11, BP-12, BP-40, BP-42, BP-43)
 * ============================================================================
 *
 * Vier Auskünfte, die eine Prüfung verlangt und die sonst niemand aufruft.
 * Sie stehen deshalb zusammen und nicht verstreut in den Fachmasken: Wer sie
 * braucht, braucht sie alle und meist am selben Tag.
 *
 * ============================================================================
 * JEDER BERICHT NENNT SEINE GRENZE
 * ============================================================================
 *
 * Das ist die wichtigste Eigenschaft dieser Maske. Ein Bericht über einen
 * Zeitraum, für den es keine Aufzeichnung gibt, zeigt NICHTS an — und
 * „nichts" liest sich wie „es ist nichts passiert". Über der
 * Konfigurationshistorie und über den Berechtigungen steht deshalb, ab wann
 * das Protokoll trägt; im Kontrollsystem steht, welche Kontrolle im Zeitraum
 * nie lief.
 *
 * Eine Auskunft, die ihre Grenze zeigt, ist mehr wert als eine, die sie
 * verschweigt — vor einem Prüfer ganz besonders.
 */
export function Betriebspruefung(): JSX.Element {
  /*
   * Die Maske oeffnet auf der AUSWERTUNG (BP-03, Z2) — der Frage, mit der
   * eine Betriebspruefung anfaengt. Die uebrigen Reiter sind Nachweise
   * darueber, wie das Haus arbeitet.
   */
  const [reiter, setzeReiter] = useState<Reiter>('auswertung');
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);

  const [staende, setzeStaende] = useState<readonly Monatsstand[]>([]);
  const [kontrollen, setzeKontrollen] = useState<readonly Kontrollnachweis[]>([]);
  const [aenderungen, setzeAenderungen] = useState<readonly Konfigurationsaenderung[]>([]);
  const [zeilen, setzeZeilen] = useState<readonly Rechtezeile[]>([]);
  const [beginn, setzeBeginn] = useState<string | null>(null);
  /* BP-03 (Z2): die Auswertung des mittelbaren Zugriffs. */
  const [summen, setzeSummen] = useState<readonly Z2Summe[]>([]);
  const [belege, setzeBelege] = useState<readonly Z2Beleg[]>([]);
  const [ohneKonto, setzeOhneKonto] = useState(0);
  const [grenze, setzeGrenze] = useState(0);
  const [merkmale, setzeMerkmale] = useState<Z2Merkmale | null>(null);
  const [filter, setzeFilter] = useState<{
    belegart: string;
    steuerschluessel: string;
    konto: string;
  }>({ belegart: '', steuerschluessel: '', konto: '' });
  /* BP-04 bis BP-08 (Z3): die Datentraegerueberlassung. */
  const [vermerke, setzeVermerke] = useState<readonly Exportvermerk[]>([]);
  const [datenbereiche, setzeDatenbereiche] = useState<
    readonly { kennung: string; name: string; beschreibung: string }[]
  >([]);
  const [zeitraum, setzeZeitraum] = useState<{ von: string; bis: string }>(() => {
    const bis = new Date();
    const von = new Date(bis.getTime() - 90 * 86_400_000);
    return { von: von.toISOString().slice(0, 10), bis: bis.toISOString().slice(0, 10) };
  });

  const holen = useCallback(async () => {
    setzeFehler(null);
    try {
      if (reiter === 'auswertung') {
        const [antwort, m] = await Promise.all([
          ladeAuswertung({
            von: zeitraum.von,
            bis: zeitraum.bis,
            ...(filter.belegart !== '' ? { belegart: filter.belegart } : {}),
            ...(filter.steuerschluessel !== ''
              ? { steuerschluessel: filter.steuerschluessel }
              : {}),
            ...(filter.konto !== '' ? { konto: filter.konto } : {}),
          }),
          ladeMerkmale(),
        ]);
        setzeSummen(antwort.summen);
        setzeBelege(antwort.belege);
        setzeOhneKonto(antwort.ohneKonto);
        setzeGrenze(antwort.grenze);
        setzeMerkmale(m);
      } else if (reiter === 'datentraeger') {
        /* `ladeMerkmale` auch hier: LEG-07 braucht die Belegarten aus dem Bestand. */
        const [antwort, b, m] = await Promise.all([
          ladeDatentraeger(),
          ladeDatenbereiche(),
          ladeMerkmale(),
        ]);
        setzeVermerke(antwort.vermerke);
        setzeDatenbereiche([...b.bereiche]);
        setzeMerkmale(m);
      } else if (reiter === 'festschreibung') {
        setzeStaende((await ladeFestschreibung()).staende);
      } else if (reiter === 'kontrollen') {
        setzeKontrollen((await ladeKontrollen(zeitraum.von, zeitraum.bis)).kontrollen);
      } else if (reiter === 'konfiguration') {
        const antwort = await ladeKonfiguration(zeitraum.von, zeitraum.bis);
        setzeAenderungen(antwort.aenderungen);
        setzeBeginn(antwort.protokollBeginn);
      } else {
        const antwort = await ladeRechte(zeitraum.von, zeitraum.bis);
        setzeZeilen(antwort.zeilen);
        setzeBeginn(antwort.protokollBeginn);
      }
    } catch (u) {
      if (!(u instanceof ApiFehler)) throw u;
      setzeFehler(u);
    }
  }, [reiter, zeitraum, filter]);

  useEffect(() => {
    void holen();
  }, [holen]);

  return (
    /*
     * SEITENTYP EINSTELLUNGEN (ÄA-41): die Reiter oben wie im Entwurf, darunter
     * je Thema eine Karte — obwohl hier nichts eingestellt wird. Es sind
     * Nachweise je Thema, und der Reiter trennt sie so, wie der Entwurf es für
     * die Kundenakte tut (Seitenplan, „Wo der Plan vom Vorschlag abweicht").
     */
    <div className="stapel">
      {fehler !== null && <Meldung fehler={fehler} />}

      <Reiterleiste
        beschriftung="Nachweise der Betriebsprüfung"
        reiter={REITER}
        gewaehlt={reiter}
        beiWahl={setzeReiter}
      />

      <div
        className="einstellungen"
        role="tabpanel"
        id={`tafel-${reiter}`}
        aria-labelledby={`reiter-${reiter}`}
      >
        {reiter !== 'festschreibung' && (
          /*
           * Beim Datenträger steht der Zeitraum NEBEN dem Formular, das ihn
           * benutzt; sonst über der ganzen Breite, über dem Bericht.
           */
          <Thema
            titel={reiter === 'auswertung' ? 'Zeitraum und Auswahl' : 'Zeitraum'}
            breit={reiter !== 'datentraeger'}
          >
            <div className="pruefzeitraum">
              <label className="liste__filter">
                Von{' '}
                <input
                  type="date"
                  aria-label="Zeitraum von"
                  value={zeitraum.von}
                  onChange={(e) => {
                    setzeZeitraum((z) => ({ ...z, von: e.target.value }));
                  }}
                />
              </label>
              <label className="liste__filter">
                Bis{' '}
                <input
                  type="date"
                  aria-label="Zeitraum bis"
                  value={zeitraum.bis}
                  onChange={(e) => {
                    setzeZeitraum((z) => ({ ...z, bis: e.target.value }));
                  }}
                />
              </label>
              {/* BP-03: Belegart, Steuersatz und Konto gehören zur Frage an Z2. */}
              {reiter === 'auswertung' && (
                <Auswahlfilter merkmale={merkmale} filter={filter} beiFilter={setzeFilter} />
              )}
              {beginn !== null && reiter !== 'auswertung' && reiter !== 'datentraeger' && (
                <p className="hilfe">
                  {/* Die Grenze der Auskunft — sonst liest sich „nichts" wie „nichts passiert". */}
                  Das Änderungsprotokoll trägt seit {zeitpunkt(beginn)}. Über die Zeit davor
                  kann dieser Bericht nichts aussagen.
                </p>
              )}
            </div>
          </Thema>
        )}

        {reiter === 'auswertung' && (
          <Auswertung summen={summen} belege={belege} ohneKonto={ohneKonto} grenze={grenze} />
        )}
        {reiter === 'datentraeger' && (
          <Datentraeger
            vermerke={vermerke}
            bereiche={datenbereiche}
            belegarten={merkmale?.belegarten ?? []}
            zeitraum={zeitraum}
            beiLauf={() => void holen()}
          />
        )}
        {reiter === 'festschreibung' && <Festschreibung staende={staende} />}
        {reiter === 'kontrollen' && <Kontrollsystem kontrollen={kontrollen} />}
        {reiter === 'konfiguration' && <Konfiguration aenderungen={aenderungen} />}
        {reiter === 'rechte' && <Berechtigungen zeilen={zeilen} />}
      </div>
    </div>
  );
}

/**
 * Nicht festgeschriebene Belege je Belegmonat (BP-11, BP-12).
 *
 * NACH MONAT UND NICHT NACH BELEG. Die Buchhaltung schreibt einen Monat fest,
 * nicht eine Rechnung — und vierhundert Zeilen beantworten die Frage „was ist
 * dran" schlechter als vier.
 */
function Festschreibung({ staende }: { readonly staende: readonly Monatsstand[] }): JSX.Element {
  if (staende.length === 0) {
    return (
      <Thema titel="Offene Belegmonate" zusatz="keine" breit>
        <p className="hinweiszeile">
          Alle Belege sind festgeschrieben. Die Frist endet nach § 146 AO mit dem letzten Tag
          des Folgemonats.
        </p>
      </Thema>
    );
  }

  return (
    <Thema titel="Offene Belegmonate" zusatz={staende.length} breit>
      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle">
          <thead>
            <tr>
              <th scope="col">Belegmonat</th>
              <th scope="col">Belege</th>
              <th scope="col">Frist</th>
              <th scope="col">Stand</th>
            </tr>
          </thead>
          <tbody>
            {staende.map((s) => (
              <tr key={s.monat}>
                <td>{monatstext(s.monat)}</td>
                <td>{s.belege.length}</td>
                <td className="liste__einzeilig">{tag(s.fristBis)}</td>
                <td>
                  {s.tageVerbleibend < 0 ? (
                    <span className="marke marke--krit">
                      seit {Math.abs(s.tageVerbleibend)} Tagen abgelaufen
                    </span>
                  ) : s.tageVerbleibend <= 7 ? (
                    <span className="marke marke--warn">noch {s.tageVerbleibend} Tage</span>
                  ) : (
                    <span className="marke">noch {s.tageVerbleibend} Tage</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="hilfe">
        Fehlen Pflichtangaben nach § 14 UStG, verhindern sie die Festschreibung — sie
        lassen sich bis zum Fristende nachtragen.
      </p>
    </Thema>
  );
}

/**
 * Das interne Kontrollsystem (BP-42).
 *
 * DIE ZAHL IST DER NACHWEIS. „Eingerichtet" steht in der Tabelle, „gelaufen"
 * wird gezählt — und eine Kontrolle mit null Ausführungen im Zeitraum ist
 * genau die Zeile, nach der ein Prüfer sucht.
 */
function Kontrollsystem({
  kontrollen,
}: {
  readonly kontrollen: readonly Kontrollnachweis[];
}): JSX.Element {
  return (
    <Thema titel="Eingerichtete Kontrollen" zusatz={kontrollen.length} breit>
      <ul className="kontrollen">
        {kontrollen.map((k) => (
          <li key={k.kennung} className="kontrollen__eintrag">
            <div className="kontrollen__kopf">
              <strong>{k.bezeichnung}</strong>{' '}
              <span className="marke">{KONTROLLART[k.art] ?? k.art}</span>{' '}
              {!k.aktiv && <span className="marke marke--inaktiv">stillgelegt</span>}
              {k.aktiv && k.imZeitraum && k.ausfuehrungen === 0 && (
                <span className="marke marke--warn">im Zeitraum nicht gelaufen</span>
              )}
            </div>
            <p className="hilfe">{k.beschreibung}</p>
            <p className="hilfe">
              <em>Verhindert:</em> {k.wirkung}
            </p>
            <p className="hilfe">
              {k.imZeitraum ? (
                <>
                  {k.ausfuehrungen} {k.ausfuehrungen === 1 ? 'Ausführung' : 'Ausführungen'}
                  {k.zuletzt !== null && `, zuletzt am ${tag(k.zuletzt)}`}
                </>
              ) : (
                /* Ein Zustand, kein Ereignis — die Nummernlückenanalyse etwa. */
                <>Bestand: {k.ausfuehrungen} Einträge (fortlaufend, ohne Zeitpunkt)</>
              )}{' '}
              · Nachweis in {k.nachweisTabelle}
            </p>
            {k.stillgelegtGrund !== null && (
              <p className="warnung">
                Stillgelegt am {tag(k.stillgelegtAm ?? '')}: {k.stillgelegtGrund}
              </p>
            )}
          </li>
        ))}
      </ul>
    </Thema>
  );
}

/**
 * Die Änderungshistorie der steuerrelevanten Konfiguration (BP-40).
 *
 * MIT ALTEM WERT. Das verlangt BP-40 ausdrücklich, und es ist die einzige
 * Angabe, mit der sich beurteilen lässt, ob eine Änderung erheblich war.
 */
function Konfiguration({
  aenderungen,
}: {
  readonly aenderungen: readonly Konfigurationsaenderung[];
}): JSX.Element {
  if (aenderungen.length === 0) {
    return (
      <Thema titel="Änderungen an der steuerrelevanten Konfiguration" zusatz="keine" breit>
        <p className="hinweiszeile">
          In diesem Zeitraum wurde an Steuerschlüsseln, Nummernkreisen, Kontenzuordnungen,
          Belegvorlagen und am Kontrollsystem nichts geändert.
        </p>
      </Thema>
    );
  }

  return (
    <Thema
      titel="Änderungen an der steuerrelevanten Konfiguration"
      zusatz={aenderungen.length}
      breit
    >
      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle">
          <thead>
            <tr>
              <th scope="col">Zeitpunkt</th>
              <th scope="col">Gegenstand</th>
              <th scope="col">Was</th>
              <th scope="col">Wer</th>
            </tr>
          </thead>
          <tbody>
            {aenderungen.map((a) => (
              <tr key={a.id}>
                <td className="liste__einzeilig">{zeitpunkt(a.zeitpunkt)}</td>
                <td>
                  {a.gegenstand}
                  <div className="hilfe">{a.datensatz}</div>
                </td>
                <td>
                  <span className="marke">{VORGANG[a.operation] ?? a.operation}</span>
                  {a.felder.length > 0 && (
                    <ul className="kontrollen__felder">
                      {a.felder.map((f) => (
                        <li key={f.feld}>
                          <strong>{f.feld}</strong>: {f.vorher ?? '—'} → {f.nachher ?? '—'}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td>
                  {a.benutzer ?? '—'}
                  {a.ausloeser !== null && <div className="hilfe">{a.ausloeser}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Thema>
  );
}

/**
 * Wer in einem Zeitraum welche Rechte hatte (BP-43).
 *
 * VERBOTE STEHEN DANEBEN UND SIND NICHT VERRECHNET. Die Begründung steht in
 * Migration 0221: Intervallarithmetik, die sich irrt, sieht richtig aus. Der
 * Leser sieht beides und zieht den Schluss selbst.
 */
function Berechtigungen({ zeilen }: { readonly zeilen: readonly Rechtezeile[] }): JSX.Element {
  if (zeilen.length === 0) {
    return (
      <Thema titel="Wer wann was durfte" zusatz="keine Aufzeichnung" breit>
        <p className="hinweiszeile">
          In diesem Zeitraum ist keine Rechtevergabe aufgezeichnet. Prüfen Sie den Zeitraum
          gegen den Beginn des Protokolls oben.
        </p>
      </Thema>
    );
  }

  return (
    <Thema titel="Wer wann was durfte" zusatz={`${zeilen.length} Zeiträume`} breit>
      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle">
          <thead>
            <tr>
              <th scope="col">Benutzer</th>
              <th scope="col">Recht</th>
              <th scope="col">Woher</th>
              <th scope="col">Von</th>
              <th scope="col">Bis</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={`${z.benutzer}-${z.objekt}-${z.aktion}-${z.von}-${z.herkunft}`}>
                <td>
                  {z.name}
                  <div className="hilfe">{z.anmeldename}</div>
                </td>
                <td>
                  {z.objekt} · {z.aktion}
                </td>
                <td>
                  {z.herkunft === 'Verbot' ? (
                    <span className="marke marke--krit">Verbot</span>
                  ) : (
                    <span className="marke">{z.herkunft}</span>
                  )}
                  <div className="hilfe">{z.quelle}</div>
                </td>
                <td className="liste__einzeilig">{tag(z.von)}</td>
                <td className="liste__einzeilig">{z.bis === null ? <span className="hilfe">offen</span> : tag(z.bis)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="hilfe">
        Ein Recht über eine Rolle gilt nur, solange beides gilt: die Zuweisung der Rolle und
        das Recht an der Rolle. Ein Verbot hebt ein Recht auf; es steht als eigene Zeile
        daneben und ist bewusst nicht gegengerechnet.
      </p>
    </Thema>
  );
}


/**
 * ============================================================================
 * DER MITTELBARE ZUGRIFF (BP-03, Z2)
 * ============================================================================
 *
 * „Frei filterbar nach Zeitraum, Belegart, Steuersatz, Kunde und Konto, mit
 * Summenbildung und Drill-down bis zum Einzelbeleg."
 *
 * ============================================================================
 * DIE SUMMEN STEHEN OBEN, DIE BELEGE DARUNTER — UND SIE MÜSSEN AUFGEHEN
 * ============================================================================
 *
 * Das ist der Fall, der die ganze Maske trägt: Ein Prüfer rechnet nach.
 * Addiert sich die Liste nicht zur Summe darüber, glaubt er danach keiner
 * Zahl mehr — zu Recht.
 *
 * Deshalb kommen beide aus EINER Antwort und nicht aus zwei Aufrufen, und
 * deshalb sagt die Maske ausdrücklich, wenn die Liste gekürzt ist: Eine
 * stillschweigend gekürzte Liste sieht aus wie eine, die nicht aufgeht.
 */
function Auswertung({
  summen,
  belege,
  ohneKonto,
  grenze,
}: {
  readonly summen: readonly Z2Summe[];
  readonly belege: readonly Z2Beleg[];
  readonly ohneKonto: number;
  readonly grenze: number;
}): JSX.Element {
  const summe = (nimm: (z: Z2Summe) => string): number =>
    summen.reduce((s, z) => s + Number(nimm(z)), 0);

  if (summen.length === 0) {
    return (
      <Thema titel="Summen" zusatz="keine" breit>
        <p className="hinweiszeile">
          In diesem Zeitraum steht kein festgeschriebener Beleg. Entwürfe zählen nicht mit —
          sie sind kein Umsatz.
        </p>
      </Thema>
    );
  }

  return (
    <>
      <Thema titel="Summen" zusatz="je Belegart, Steuersatz und Konto" breit>
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="hilfe">
              Summen je Belegart, Steuersatz und Konto. Storno und Gutschrift mindern.
            </caption>
            <thead>
              <tr>
                <th scope="col">Belegart</th>
                <th scope="col">Steuersatz</th>
                <th scope="col">Konto</th>
                <th scope="col">Belege</th>
                <th scope="col">Netto</th>
                <th scope="col">Steuer</th>
                <th scope="col">Brutto</th>
              </tr>
            </thead>
            <tbody>
              {summen.map((z) => (
                <tr key={`${z.belegart}-${z.steuerschluessel ?? ''}-${z.konto ?? ''}`}>
                  <td>{z.belegart}</td>
                  <td>{z.steuerschluessel ?? '—'}</td>
                  <td>{z.konto ?? '—'}</td>
                  <td className="zahl">{z.belege}</td>
                  <td className="zahl">{alsEuro(z.netto)}</td>
                  <td className="zahl">{alsEuro(z.steuer)}</td>
                  <td className="zahl">{alsEuro(z.brutto)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={4}>
                  Zusammen
                </th>
                <td className="zahl">{alsEuro(String(summe((z) => z.netto)))}</td>
                <td className="zahl">{alsEuro(String(summe((z) => z.steuer)))}</td>
                <td className="zahl">{alsEuro(String(summe((z) => z.brutto)))}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/*
          EINE STAMMDATENLÜCKE IST KEIN ERGEBNIS.
          Ein Kontofilter, der nichts findet, ist etwas anderes als ein Konto
          ohne Umsatz — und der Unterschied gehört dem Leser gesagt.
        */}
        {ohneKonto > 0 && (
          <p className="hinweiszeile">
            {ohneKonto} Beleg{ohneKonto === 1 ? '' : 'e'} in diesem Zeitraum tragen kein
            Erlöskonto. Das Konto steht am Artikel; solange es dort fehlt, findet der
            Kontofilter sie nicht.
          </p>
        )}
      </Thema>

      <Thema titel="Einzelbelege" zusatz={belege.length} breit>
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="hilfe">
              Die Einzelbelege dazu — sie addieren sich zur Summe darüber.
            </caption>
            <thead>
              <tr>
                <th scope="col">Beleg</th>
                <th scope="col">Datum</th>
                <th scope="col">Art</th>
                <th scope="col">Kunde</th>
                <th scope="col">Netto</th>
                <th scope="col">Steuer</th>
                <th scope="col">Brutto</th>
              </tr>
            </thead>
            <tbody>
              {belege.map((b) => (
                <tr key={b.id}>
                  <td>{b.rechnungsnummer ?? '—'}</td>
                  <td className="liste__einzeilig">{tag(b.belegdatum)}</td>
                  <td>{b.belegart}</td>
                  <td>{b.kundenname}</td>
                  <td className="zahl">{alsEuro(b.netto)}</td>
                  <td className="zahl">{alsEuro(b.steuer)}</td>
                  <td className="zahl">{alsEuro(b.brutto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {belege.length >= grenze && grenze > 0 && (
          <p className="hinweiszeile">
            Die Liste zeigt die neuesten {grenze} Belege. Die Summen darüber sind
            vollständig — grenzen Sie den Zeitraum ein, um alle Belege zu sehen.
          </p>
        )}
      </Thema>
    </>
  );
}

type Z2Filter = { belegart: string; steuerschluessel: string; konto: string };

/**
 * Die Auswahl des mittelbaren Zugriffs (BP-03): Belegart, Steuersatz und
 * Konto. Sie steht neben dem Zeitraum in derselben Karte — zusammen sind sie
 * die Frage, auf die Summen und Einzelbelege darunter antworten.
 */
function Auswahlfilter({
  merkmale,
  filter,
  beiFilter,
}: {
  readonly merkmale: Z2Merkmale | null;
  readonly filter: Z2Filter;
  readonly beiFilter: (f: Z2Filter) => void;
}): JSX.Element {
  return (
    <>
      <label className="liste__filter">
        Belegart{' '}
        <select
          aria-label="Belegart"
          value={filter.belegart}
          onChange={(e) => {
            beiFilter({ ...filter, belegart: e.target.value });
          }}
        >
          <option value="">Alle Belegarten</option>
          {(merkmale?.belegarten ?? []).map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </label>
      <label className="liste__filter">
        Steuersatz{' '}
        <select
          aria-label="Steuersatz"
          value={filter.steuerschluessel}
          onChange={(e) => {
            beiFilter({ ...filter, steuerschluessel: e.target.value });
          }}
        >
          <option value="">Alle Steuersätze</option>
          {(merkmale?.steuerschluessel ?? []).map((s) => (
            <option key={s.schluessel} value={s.schluessel}>
              {s.bezeichnung}
            </option>
          ))}
        </select>
      </label>
      <label className="liste__filter">
        Konto{' '}
        <select
          aria-label="Konto"
          value={filter.konto}
          onChange={(e) => {
            beiFilter({ ...filter, konto: e.target.value });
          }}
        >
          <option value="">Alle Konten</option>
          {(merkmale?.konten ?? []).map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </label>
    </>
  );
}


/**
 * ============================================================================
 * DIE DATENTRAEGERUEBERLASSUNG (BP-04 bis BP-08)
 * ============================================================================
 *
 * Z3 ist in der Praxis der Regelfall: Der Prüfer bekommt einen Datenträger
 * und wertet ihn im eigenen Haus aus. Ein Export, den die Prüfsoftware nicht
 * einlesen kann, gilt als nicht erfüllte Mitwirkungspflicht.
 *
 * ============================================================================
 * DIE PROBE IST DER VORGABEWERT — UND DAS IST ABSICHT
 * ============================================================================
 *
 * Ein Häkchen, das versehentlich eine Übergabe an das Finanzamt
 * protokolliert, wäre schlimmer als eines, das eine Probe zu viel schreibt.
 * Ein Exportvermerk lässt sich nicht löschen (BP-07): Er ist der Nachweis
 * einer Übergabe, und was hier steht, steht in acht Jahren noch da.
 *
 * Wer wirklich übergibt, nimmt das Häkchen weg und nennt den Empfänger — und
 * genau dann verlangt die Maske ihn auch.
 */
function Datentraeger({
  vermerke,
  bereiche,
  belegarten,
  zeitraum,
  beiLauf,
}: {
  readonly vermerke: readonly Exportvermerk[];
  readonly bereiche: readonly { kennung: string; name: string; beschreibung: string }[];
  /** LEG-07: die Belegarten aus dem Bestand — dieselbe Quelle wie bei Z2. */
  readonly belegarten: readonly string[];
  readonly zeitraum: { von: string; bis: string };
  readonly beiLauf: () => void;
}): JSX.Element {
  const [gewaehlt, setzeGewaehlt] = useState<readonly string[]>([]);
  /* LEG-07: leer heisst alle Belegarten — der Regelfall. */
  const [belegart, setzeBelegart] = useState('');
  const [istProbe, setzeIstProbe] = useState(true);
  const [empfaenger, setzeEmpfaenger] = useState('');
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);

  /* Beim ersten Erscheinen sind alle Bereiche gewaehlt: BP-04 sagt „alle". */
  useEffect(() => {
    if (bereiche.length > 0 && gewaehlt.length === 0) {
      setzeGewaehlt(bereiche.map((b) => b.kennung));
    }
  }, [bereiche, gewaehlt.length]);

  async function erzeugen(): Promise<void> {
    setzeLaeuft(true);
    setzeFehler(null);
    setzeMeldung(null);
    try {
      const vermerk = await erzeugeDatentraeger({
        von: zeitraum.von,
        bis: zeitraum.bis,
        bereiche: gewaehlt,
        ...(belegart === '' ? {} : { belegart }),
        ...(istProbe ? {} : { empfaenger }),
        istProbe,
      });
      setzeMeldung(
        `${vermerk.dateien.length} Dateien mit ${vermerk.zeilenGesamt} Datenzeilen erzeugt.`,
      );
      beiLauf();
    } catch (u) {
      if (!(u instanceof ApiFehler)) throw u;
      setzeFehler(u);
    } finally {
      setzeLaeuft(false);
    }
  }

  async function herunterladen(id: string, name: string): Promise<void> {
    try {
      // Auftrag CRM-168: ein PDF oeffnet gleich im Betrachter, alles andere bleibt Download.
      const hinweis = await ladeUndReiche(
        async () => ({ blob: await holeDatei(id, name), dateiname: name }),
        istPdfName(name),
      );
      if (hinweis !== null) setzeMeldung(hinweis);
    } catch (u) {
      if (!(u instanceof ApiFehler)) throw u;
      setzeFehler(u);
    }
  }

  return (
    <>
      {/* BP-04 bis BP-08 */}
      <Thema titel="Datenträger erzeugen">
        {fehler !== null && <Meldung fehler={fehler} />}
        {meldung !== null && (
          <p className="hinweiszeile" role="status">
            {meldung}
          </p>
        )}

        <p className="hilfe">
          Der Datenträger enthält die steuerrelevanten Daten des gewählten Zeitraums als
          CSV-Dateien mit einer <code>index.xml</code> nach dem Beschreibungsstandard der
          Finanzverwaltung. Ein Schlüsselverzeichnis und ein Summenblatt gehen immer mit.
        </p>

        <fieldset className="wahlreihe">
          <legend>Datenbereiche</legend>
          {bereiche.map((b) => (
            <label key={b.kennung} className="liste__schalter">
              <input
                type="checkbox"
                checked={gewaehlt.includes(b.kennung)}
                onChange={(e) => {
                  setzeGewaehlt((alt) =>
                    e.target.checked ? [...alt, b.kennung] : alt.filter((k) => k !== b.kennung),
                  );
                }}
              />{' '}
              {b.name}
            </label>
          ))}
        </fieldset>

        <label className="liste__filter">
          Belegart{' '}
          <select
            aria-label="Belegart"
            value={belegart}
            onChange={(e) => {
              setzeBelegart(e.target.value);
            }}
          >
            <option value="">Alle Belegarten</option>
            {belegarten.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
        {belegart !== '' && (
          <p className="hilfe">
            Der Datenträger enthält dann nur Belege dieser Art — dazu die Debitoren und
            Zahlungen, die daran hängen. Artikel, Änderungsprotokoll und Schlüsselverzeichnis
            bleiben vollständig. Die <code>index.xml</code> sagt das auch, damit die Lücke
            nicht als Tatsache gelesen wird.
          </p>
        )}

        <label className="liste__schalter">
          <input
            type="checkbox"
            checked={istProbe}
            onChange={(e) => {
              setzeIstProbe(e.target.checked);
            }}
          />{' '}
          Prüfsimulation — der Datenträger wird nicht übergeben
        </label>

        {!istProbe && (
          <div className="feld">
            <label htmlFor="z3-empfaenger">Empfänger</label>
            <input
              id="z3-empfaenger"
              type="text"
              className="feld__eingabe"
              value={empfaenger}
              onChange={(e) => {
                setzeEmpfaenger(e.target.value);
              }}
            />
            <p className="feld__hilfe">
              Finanzamt und Prüfer. Der Empfänger steht dauerhaft im Protokoll — Jahre später
              ist das die Frage, die gestellt wird.
            </p>
          </div>
        )}

        <div className="liste__leiste">
          <button
            type="button"
            className="knopf knopf--haupt"
            disabled={laeuft || gewaehlt.length === 0 || (!istProbe && empfaenger.trim() === '')}
            onClick={() => void erzeugen()}
          >
            {laeuft ? 'Wird erzeugt …' : istProbe ? 'Probelauf starten' : 'Datenträger erzeugen'}
          </button>
        </div>
      </Thema>

      <Thema titel="Überlassungen" zusatz={vermerke.length} breit>
        {vermerke.length === 0 ? (
          <p className="hinweiszeile">Es wurde noch kein Datenträger erzeugt.</p>
        ) : (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <caption className="hilfe">
                Jede Überlassung mit Zeitpunkt, Umfang, Zeitraum und Empfänger — der Vermerk
                lässt sich nicht ändern und nicht löschen.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Erzeugt</th>
                  <th scope="col">Zeitraum</th>
                  <th scope="col">Empfänger</th>
                  <th scope="col">Zeilen</th>
                  <th scope="col">Dateien</th>
                </tr>
              </thead>
              <tbody>
                {vermerke.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <span className="liste__einzeilig">{zeitpunkt(v.angelegtAm)}</span>
                      {v.angelegtVonName !== null && <small> · {v.angelegtVonName}</small>}
                    </td>
                    <td>
                      <span className="liste__einzeilig">{tag(v.von)}</span> bis{' '}
                      <span className="liste__einzeilig">{tag(v.bis)}</span>
                      {v.belegart !== null && (
                        <>
                          <br />
                          <small>nur {v.belegart}</small>
                        </>
                      )}
                    </td>
                    <td>
                      {v.istProbe ? <em>Prüfsimulation</em> : (v.empfaenger ?? '—')}
                    </td>
                    <td className="zahl">{v.zeilenGesamt}</td>
                    <td>
                      <ul className="merkmalsliste">
                        {v.dateien.map((d) => (
                          <li key={d.name}>
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              onClick={() => void herunterladen(v.id, d.name)}
                            >
                              {d.name}
                            </button>{' '}
                            <small>
                              {d.zeilen > 0 ? `${d.zeilen} Zeilen · ` : ''}
                              {d.pruefsumme.slice(0, 12)}…
                            </small>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Thema>
    </>
  );
}
